import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { Product } from '../../../interfaces/product.interface';
import { CartService } from '../../../services/cart.service';
import { DoseColorService } from '../../../services/dose-color.service';
import { ProductLocalizationService } from '../../../services/product-localization.service';
import { LanguageService } from '../../../services/language.service';
import { Subject, takeUntil, combineLatest } from 'rxjs';

@Component({
  selector: 'app-product-card',
  imports: [CommonModule, RouterModule],
  templateUrl: './product-card.html',
  styleUrl: './product-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductCard implements OnInit, OnDestroy {
  @Input() product!: Product;
  
  // Cached localized data to reduce subscriptions
  localizedName: string = '';
  localizedDescription: string = '';
  localizedBenefits: string[] = [];
  readMoreText: string = '';
  
  // Cached computed values (public for template access)
  cachedImageUrl: string = '';
  cachedGradientStyle: Record<string, string> | null = null;
  
  private destroy$ = new Subject<void>();

  constructor(
    private cartService: CartService,
    private router: Router,
    public doseColorService: DoseColorService,
    private productLocalizationService: ProductLocalizationService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Initialize cached values immediately
    this.updateLocalizedData();
    
    // Subscribe to both language and translations changes to update cached values
    // This ensures translations load properly on page refresh
    combineLatest([
      this.languageService.currentLanguage$,
      this.languageService.translations$
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.updateLocalizedData();
      this.cdr.markForCheck();
    });
    
    // Cache image URL and gradient style
    this.cachedImageUrl = this.getImageUrl(this.getFirstImage());
    this.cachedGradientStyle = this.getSafeGradientStyle();
  }

  private updateLocalizedData(): void {
    this.localizedName = this.productLocalizationService.getLocalizedName(this.product);
    this.localizedDescription = this.productLocalizationService.getLocalizedDescription(this.product);
    this.localizedBenefits = this.productLocalizationService.getLocalizedBenefits(this.product);
    this.readMoreText = this.languageService.t('buyNow');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addToCart(product: Product) {
    this.cartService.addToCart(product);
  }

  viewDetails(product: Product) {
    this.router.navigate(['/product', product.id]);
  }

  // Localized product name (non-reactive for backward compatibility)
  getLocalizedName(): string {
    return this.productLocalizationService.getLocalizedName(this.product);
  }

  // Localized product description (non-reactive for backward compatibility)
  getLocalizedDescription(): string {
    return this.productLocalizationService.getLocalizedDescription(this.product);
  }

  // Localized benefits (non-reactive for backward compatibility)
  getLocalizedBenefits(): string[] {
    return this.productLocalizationService.getLocalizedBenefits(this.product);
  }

  // Add safe dose type handling
  getSafeDoseType(): string {
    return this.product?.doseType || 'default';
  }

  // Add fallback for missing dose types
  getDoseDisplayName(): string {
    return this.getLocalizedName();
  }

  // Safe method to get gradient style
  getSafeGradientStyle(): Record<string, string> {
    try {
      if (!this.product?.doseType) {
        return {
          'background': 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
          'color': '#FFFFFF'
        };
      }
      return this.doseColorService.generateGradientStyle(this.product.doseType);
    } catch (error) {
      console.warn('Error getting gradient style:', error);
      return {
        'background': 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
        'color': '#FFFFFF'
      };
    }
  }

  // Get the first image from product handling both array and object formats
  getFirstImage(): string {
    if (!this.product?.image) return '/assets/images/default-product.png';
    
    // Handle both array and object formats
    const imageArray = Array.isArray(this.product.image) 
      ? this.product.image 
      : Object.values(this.product.image);
    
    const firstImage = imageArray[0];
    return (typeof firstImage === 'string') ? firstImage : '/assets/images/default-product.png';
  }

  // Get proper image URL handling both Firebase Storage and local paths (optimized - no console logs)
  getImageUrl(imagePath: string): string {
    if (!imagePath || imagePath === '' || imagePath === null || imagePath === undefined) {
      return '/assets/images/default-product.png';
    }
    
    // Firebase Storage URL (full URL)
    if (imagePath.startsWith('https://firebasestorage.googleapis.com/')) {
      return imagePath;
    }
    
    // Full URL or data URL
    if (imagePath.startsWith('http') || imagePath.startsWith('data:')) {
      return imagePath;
    }
    
    // Firebase Storage path (relative path like 'product-images/filename.jpg')
    if (imagePath.startsWith('product-images/')) {
      // For Firebase Storage paths, we need to construct the full URL
      // This is a limitation - in production you'd use Firebase Storage SDK to get the URL
      return '/assets/images/default-product.png'; // Fallback for now
    }
    
    // Local path - ensure it starts with ./
    if (imagePath.startsWith('./product/') || imagePath.startsWith('/product/')) {
      return imagePath.startsWith('./') ? imagePath : './' + imagePath.substring(1);
    }
    
    return '/assets/images/default-product.png';
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      console.warn('Product image failed to load:', img.src);
      img.src = '/assets/images/default-product.png';
      // If default image also fails, show a placeholder
      img.onerror = () => {
        img.style.display = 'none';
        img.parentElement?.insertAdjacentHTML('afterbegin', 
          '<div style="width: 100%; height: 200px; background: #f0f0f0; display: flex; align-items: center; justify-content: center; color: #666;">No Image</div>'
        );
      };
    }
  }

  // Translation method (kept for backward compatibility if needed)
  t(key: string): string {
    return this.languageService.t(key);
  }
}
